import PropTypes from "prop-types";
import { cloneElement, useState } from "react";
import { Phone } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import CallForm from "components/forms/CallForm";
import { DEFAULT_CAMPAIGN_ID } from "constants/Constants";

function CallModal({
  contactId,
  campaignId = DEFAULT_CAMPAIGN_ID,
  disabled = false,
  voicemails,
  forms,
  trigger,
  iconOnly = false,
  onBeforeOpen,
}) {
  const [open, setOpen] = useState(false);

  const handleOpen = async () => {
    // Lets a caller veto opening at click time (e.g. an already-active dial
    // session on the Contact page); absent for every other caller. May resolve
    // asynchronously when the caller shows its own confirmation.
    if (onBeforeOpen && (await onBeforeOpen()) === false) {
      return;
    }
    setOpen(true);
  };

  const renderTrigger = () => {
    if (trigger) {
      return cloneElement(trigger, { onClick: handleOpen, disabled });
    }
    if (iconOnly) {
      return (
        <Tooltip>
          {/* A disabled button fires no pointer events, so the span keeps the
              tooltip working. */}
          <TooltipTrigger render={<span className="tw:inline-flex" />}>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label="Call"
              onClick={handleOpen}
              disabled={disabled}
            >
              <Phone />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Call</TooltipContent>
        </Tooltip>
      );
    }
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={handleOpen}
        disabled={disabled}
      >
        <Phone data-icon="inline-start" />
        Call
      </Button>
    );
  };

  return (
    <>
      {renderTrigger()}
      <Dialog open={open} onOpenChange={setOpen} disablePointerDismissal>
        <DialogContent>
          <DialogTitle>Call Contact</DialogTitle>
          <CallForm
            contactId={contactId}
            campaignId={campaignId}
            voicemails={voicemails}
            forms={forms}
            onStarted={() => setOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

CallModal.propTypes = {
  contactId: PropTypes.number,
  campaignId: PropTypes.number,
  disabled: PropTypes.bool,
  voicemails: PropTypes.array,
  forms: PropTypes.array,
  trigger: PropTypes.element,
  iconOnly: PropTypes.bool,
  onBeforeOpen: PropTypes.func,
};

export default CallModal;
